// src/components/Reports/ProfitKit.jsx
// أدوات مشتركة لتقارير الأرباح: طرق التكلفة، خلايا الربح والهامش، التنبيهات، وشرح طريقة الاحتساب
import React, { useState } from 'react';
import { Box, Typography, Tooltip, Paper, Collapse, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { fmtMoney } from './ReportKit';

export const COST_METHODS = [
  { value: 'avg', label: 'متوسط مرجّح (موصى به)' },
  { value: 'last', label: 'آخر سعر شراء' },
  { value: 'card', label: 'سعر بطاقة الصنف' },
];
export const costMethodLabel = (v) => COST_METHODS.find(m => m.value === v)?.label?.replace(' (موصى به)', '') || 'متوسط مرجّح';

export const PROFIT_FILTERS = [
  { value: 'loss', label: 'الخاسرة فقط' },
  { value: 'warn', label: 'بها تنبيهات تكلفة' },
  { value: 'profit', label: 'الرابحة فقط' },
];

export const hasWarnings = (r) => (r.negativeStockLines || 0) + (r.noCostLines || 0) + (r.fallbackCostLines || 0) > 0
  || r.negativeStock || r.costSource === 'None' || r.costSource === 'FirstPurchase' || r.costSource === 'Card';

export const applyProfitFilter = (rows, f) => {
  if (f === 'loss') return rows.filter(r => Number(r.profit) < 0);
  if (f === 'profit') return rows.filter(r => Number(r.profit) > 0);
  if (f === 'warn') return rows.filter(hasWarnings);
  return rows;
};

/** خلية مبلغ الربح (أخضر/أحمر) */
export const ProfitCell = ({ value }) => {
  const v = Number(value) || 0;
  return (
    <Typography sx={{ direction: 'ltr', fontWeight: 800, fontSize: 13.5, color: v < 0 ? '#be123c' : v > 0 ? '#047857' : '#94a3b8' }}>
      {fmtMoney(v)}
    </Typography>
  );
};

/** خلية الهامش مع شريط */
export const MarginCell = ({ value }) => {
  const v = Number(value) || 0;
  const color = v < 0 ? '#e11d48' : v < 10 ? '#d97706' : '#059669';
  return (
    <Box sx={{ width: '100%', px: .5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color, direction: 'ltr', textAlign: 'center' }}>{v.toFixed(1)}%</Typography>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(100, Math.abs(v))}%`, height: '100%', bgcolor: color }} />
      </Box>
    </Box>
  );
};

/** شارات التنبيه لسطر مجمّع (فاتورة / صنف) */
export const WarningsCell = ({ row }) => {
  const items = [
    row.negativeStockLines > 0 && { t: `${row.negativeStockLines} بيع على المكشوف`, bg: '#fef3c7', fg: '#b45309', tip: 'بيع والرصيد غير كافٍ وقت البيع — التكلفة تقديرية' },
    row.noCostLines > 0 && { t: `${row.noCostLines} بدون تكلفة`, bg: '#ffe4e6', fg: '#be123c', tip: 'لا يوجد أي سعر شراء ولا سعر بطاقة — الربح = كامل البيع' },
    row.fallbackCostLines > 0 && { t: `${row.fallbackCostLines} تكلفة بديلة`, bg: '#e0f2fe', fg: '#0369a1', tip: 'بيع قبل أول شراء — استُخدم أول سعر شراء لاحق أو سعر البطاقة' },
  ].filter(Boolean);
  if (!items.length) return <Typography sx={{ color: '#cbd5e1', fontSize: 13 }}>—</Typography>;
  return (
    <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', justifyContent: 'center' }}>
      {items.map(i => (
        <Tooltip key={i.t} title={i.tip}>
          <Box sx={{ px: .9, py: .2, borderRadius: '999px', fontSize: 11.5, fontWeight: 800, bgcolor: i.bg, color: i.fg, whiteSpace: 'nowrap' }}>{i.t}</Box>
        </Tooltip>
      ))}
    </Box>
  );
};

/** مصدر التكلفة لسطر تفصيلي */
export const CostSourceCell = ({ row }) => {
  const map = {
    Average: ['#eef2ff', '#4338ca'], LastPurchase: ['#eef2ff', '#4338ca'], Card: ['#e0f2fe', '#0369a1'],
    FirstPurchase: ['#e0f2fe', '#0369a1'], Service: ['#f1f5f9', '#475569'], None: ['#ffe4e6', '#be123c'],
  };
  const [bg, fg] = map[row.costSource] || map.Average;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
      <Box sx={{ px: .9, py: .2, borderRadius: '999px', fontSize: 11.5, fontWeight: 800, bgcolor: bg, color: fg, whiteSpace: 'nowrap' }}>
        {row.costSourceName}
      </Box>
      {row.negativeStock && (
        <Tooltip title="بيع على المكشوف: الرصيد لم يكن كافيًا وقت البيع">
          <WarningAmberRoundedIcon sx={{ fontSize: 17, color: '#d97706' }} />
        </Tooltip>
      )}
    </Box>
  );
};

/** شرح طريقة احتساب التكلفة (قابل للطي) */
export function CostingInfo({ method, summary }) {
  const [open, setOpen] = useState(false);
  const warn = (summary?.negativeStockLines || 0) + (summary?.noCostLines || 0) + (summary?.fallbackCostLines || 0);
  return (
    <Paper elevation={0} sx={{ mb: 1.5, borderRadius: '14px', border: `1px solid ${warn ? '#fde68a' : '#e5e7eb'}`, bgcolor: warn ? '#fffbeb' : '#fff', overflow: 'hidden' }}>
      <Box onClick={() => setOpen(o => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, cursor: 'pointer' }}>
        {warn ? <WarningAmberRoundedIcon sx={{ color: '#d97706' }} /> : <InfoOutlinedIcon sx={{ color: '#4f46e5' }} />}
        <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>
          التكلفة محسوبة بطريقة: <b>{costMethodLabel(method)}</b> · المبالغ بدون ضريبة
          {warn > 0 && <> — <span style={{ color: '#b45309' }}>{warn} سطر تكلفته تقديرية</span></>}
        </Typography>
        <IconButton size="small"><ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '.2s' }} /></IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2, fontSize: 13.5, lineHeight: 1.9, color: '#334155', '& b': { color: '#0f172a' } }}>
          <div>• <b>الإيراد</b> = الكمية × السعر − الخصم (بدون ضريبة). المرتجعات تُطرح بالسالب.</div>
          <div>• <b>المتوسط المرجّح</b>: يُعاد تشغيل حركة كل صنف من أول يوم بأسعار الشراء الفعلية (بعد الخصم)، وكل بيع يأخذ متوسط التكلفة وقت البيع.</div>
          <div>• داخل اليوم الواحد تُحسب <b>المشتريات قبل المبيعات</b> حتى لا يظهر بيع على المكشوف بسبب ترتيب الإدخال فقط.</div>
          <div>• <b>البيع على المكشوف</b> (الرصيد غير كافٍ): يُكلَّف بآخر متوسط معروف، وإذا لم يُشترَ الصنف قبل ذلك إطلاقًا فبأول سعر شراء لاحق، ثم سعر البطاقة.</div>
          <div>• عند الشراء والرصيد سالب يبدأ المتوسط من سعر الشراء الجديد — لا يُخلط برصيد سالب.</div>
          <div>• مرتجع المبيعات يعود بالمتوسط وقت الإرجاع، ومرتجع المشتريات يخرج بسعره الفعلي.</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mt: 1, color: '#64748b' }}>
            <HelpOutlineIcon sx={{ fontSize: 17 }} />
            الربح هنا تقديري لأغراض الإدارة. الربح المحاسبي النهائي في الجرد الدوري = المبيعات − (مخزون أول المدة + المشتريات − مخزون آخر المدة الفعلي بعد الجرد).
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
